import clsx from 'clsx';
import { useMemo } from 'react';
import {
  generateBuriedPointProducts,
  ProductCard,
  useBuriedPoint,
  useProductListApi,
  type BuriedPoint,
} from '../../../common';
import { useHeaderData, selectSearchHotKey } from '../../../header';
import { useRelatedSearchDataApi } from '../../api';
import { useSearchStore } from '../../stores';
import { generateSearchPagePath } from '../../utils';
import { Container } from './Container';
import { Clear } from './Clear';
import { Keyword } from './Keyword';

export function SearchPanel() {
  const keyword = useSearchStore.use.keyword();
  const isSearchOpen = useSearchStore.use.isSearchOpen();
  const recentSearch = useSearchStore.use.recentSearch();
  const recentBrowse = useSearchStore.use.recentBrowse();
  const clearRecentSearch = useSearchStore.use.clearRecentSearch();
  const clearRecentBrowse = useSearchStore.use.clearRecentBrowse();

  const headerData = useHeaderData();
  const hotKey = useMemo(() => selectSearchHotKey(headerData), [headerData]);

  const productListApiState = useProductListApi(
    { skus: recentBrowse.join(',') },
    { enabled: isSearchOpen && recentBrowse.length > 0 }
  );
  const recentBrowseProducts = productListApiState?.data?.data?.skuData;

  const relatedSearchDataApiState = useRelatedSearchDataApi({ k: keyword });
  const relatedData = relatedSearchDataApiState.data?.data;
  const isCompleted = Boolean(keyword && relatedSearchDataApiState.isFetched);

  const searchSuggest = {
    label: isCompleted ? '搜尋建議' : '熱門搜尋',
    items: isCompleted ? relatedData?.autocomplete : hotKey,
  };
  const relatedCategory = {
    label: isCompleted ? '相關分類' : '歷史搜尋',
    items: isCompleted ? relatedData?.relatedCategory : recentSearch,
    clear: isCompleted ? null : <Clear onClick={clearRecentSearch} />,
  };
  const relatedProduct = {
    label: isCompleted ? '相關商品' : '最近瀏覽',
    items: isCompleted ? relatedData?.relatedProduct : recentBrowseProducts,
    clear: isCompleted ? null : <Clear onClick={clearRecentBrowse} />,
  };

  const isSearchSuggestVisible = Boolean(searchSuggest.items?.length);
  const isRelatedCategoryVisible = Boolean(relatedCategory.items?.length);
  const isRelatedProductVisible = Boolean(relatedProduct.items?.length);
  const isTextAreaVisible = isSearchSuggestVisible || isRelatedCategoryVisible;

  const { tagAttributes } = useBuriedPoint('prodlist', {
    data: useMemo<Partial<BuriedPoint>>(
      () => ({ items: generateBuriedPointProducts(recentBrowseProducts) }),
      [recentBrowseProducts]
    ),
    enabled: !isCompleted,
  });

  return (
    <div
      className={clsx(
        'tlw-scrollbar tlw-h-[463px] tlw-space-y-3 tlw-bg-white tlw-rounded-xl tlw-overflow-y-auto lg:tlw-space-y-0 lg:tlw-divide-x lg:tlw-divide-gray-300',
        isTextAreaVisible && 'lg:tlw-grid lg:tlw-grid-cols-[4fr_6fr]'
      )}
    >
      {isTextAreaVisible && (
        <div className="tlw-pr-4 tlw-pl-6 tlw-grid tlw-grid-cols-2 tlw-gap-6 lg:tlw-py-3 lg:tlw-px-6 lg:tlw-flex lg:tlw-flex-col lg:tlw-scrollbar lg:tlw-overflow-y-auto lg:tlw-overscroll-contain">
          {/* 熱門搜尋 or 搜尋建議 */}
          {isSearchSuggestVisible && (
            <Container label={searchSuggest.label}>
              <ul className="tlw-py-1 tlw-px-2">
                {searchSuggest.items?.map((item) => {
                  const isText = typeof item === 'string';
                  const key = isText ? item : item.mainHeading;
                  const href = isText
                    ? generateSearchPagePath(item)
                    : item.link;
                  return (
                    <li key={key} className="tlw-p-1">
                      <Keyword href={href}>{key}</Keyword>
                    </li>
                  );
                })}
              </ul>
            </Container>
          )}

          {/* 歷史搜尋 or 相關分類 */}
          {isRelatedCategoryVisible && (
            <Container
              label={relatedCategory.label}
              extra={relatedCategory.clear}
            >
              <ul className="tlw-py-1 tlw-px-2">
                {relatedCategory.items?.map((item) => {
                  const isText = typeof item === 'string';
                  const key = isText ? item : item.categoryName;
                  const href = isText
                    ? generateSearchPagePath(item)
                    : item.link;
                  return (
                    <li key={key} className="tlw-p-1">
                      <Keyword href={href}>{key}</Keyword>
                    </li>
                  );
                })}
              </ul>
            </Container>
          )}
        </div>
      )}

      {/* 最近瀏覽 or 相關商品 */}
      {isRelatedProductVisible && (
        <Container
          className="tlw-pr-4 tlw-pb-4 tlw-pl-6 lg:tlw-scrollbar lg:tlw-py-3 lg:tlw-px-8 lg:tlw-space-y-2 lg:tlw-overflow-y-auto"
          label={relatedProduct.label}
          extra={relatedProduct.clear}
        >
          <div {...tagAttributes}>
            {relatedProduct.items?.map((item) => (
              <ProductCard
                key={item.sku}
                className="tlw-grid-cols-[115px_1fr] tlw-mt-4 first:tlw-mt-0"
                product={item}
                mode="horizontal"
                catalog={tagAttributes['data-id-catalog']}
                showFavorite={false}
                squarePreview={false}
              />
            ))}
          </div>
        </Container>
      )}
    </div>
  );
}
